import React, { useState } from "react";
import { useHR } from "../HRContext";
import { HRPosition, HREmployee } from "../../types/hr";
import PositionForm from "./PositionForm";
import {
  Layout,
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  List,
  Empty,
  Modal,
  message,
  Spin,
  Statistic,
  Divider,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  BankOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PositionList: React.FC = () => {
  const { positions, employees, departments, loading, deletePosition } =
    useHR();
  const [showForm, setShowForm] = useState(false);
  const [editingPosition, setEditingPosition] = useState<HRPosition | null>(
    null
  );
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(
    null
  );

  const handleDelete = async (position: HRPosition) => {
    const employeeCount = employees.filter(
      (emp: HREmployee) => emp.position_id === position.id
    ).length;

    if (employeeCount > 0) {
      Modal.warning({
        title: "Suppression impossible",
        content: `Impossible de supprimer ce poste car ${employeeCount} employé(s) y sont rattaché(s).`,
      });
      return;
    }

    Modal.confirm({
      title: "Confirmation de suppression",
      content: `Êtes-vous sûr de vouloir supprimer le poste "${position.title}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await deletePosition(position.id);
          message.success("Poste supprimé avec succès");
        } catch (error) {
          message.error("Erreur lors de la suppression du poste");
        }
      },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-BF", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Filtrer les postes par département sélectionné
  const filteredPositions = selectedDepartment
    ? positions.filter(
        (pos: HRPosition) => pos.department_id === selectedDepartment
      )
    : positions;

  if (loading.positions) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* Header */}
        <Card className="mb-6" bordered={false}>
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="!mb-2">
                Gestion des Postes
              </Title>
              <Text type="secondary">
                {filteredPositions.length} poste(s)
                {selectedDepartment && " dans le département sélectionné"}
              </Text>
            </div>
            <div className="flex items-center space-x-3">
              {/* Filtre par département */}
              <Select
                placeholder="Filtrer par département"
                allowClear
                style={{ width: 200 }}
                value={selectedDepartment}
                onChange={setSelectedDepartment}
              >
                {departments.map((dept) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingPosition(null);
                  setShowForm(true);
                }}
              >
                Nouveau Poste
              </Button>
            </div>
          </div>
        </Card>

        {/* Grille des postes */}
        {filteredPositions.length > 0 ? (
          <Row gutter={[16, 16]}>
            {filteredPositions.map((position: HRPosition) => {
              const positionEmployees = employees.filter(
                (emp: HREmployee) => emp.position_id === position.id
              );
              const department = departments.find(
                (dept) => dept.id === position.department_id
              );

              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={position.id}>
                  <Card
                    hoverable
                    actions={[
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingPosition(position);
                          setShowForm(true);
                        }}
                        title="Modifier"
                      />,
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(position)}
                        title="Supprimer"
                      />,
                    ]}
                    className="h-full"
                  >
                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <BankOutlined className="text-purple-600" />
                        <Title level={4} className="!mb-0">
                          {position.title}
                        </Title>
                      </div>
                      {position.description && (
                        <Paragraph ellipsis={{ rows: 2 }} className="!mb-0">
                          {position.description}
                        </Paragraph>
                      )}
                    </div>

                    {/* Département */}
                    {department && (
                      <div className="mb-3">
                        <Text type="secondary" className="text-sm">
                          Département :
                        </Text>
                        <div className="mt-1">
                          <Tag color="blue">{department.name}</Tag>
                        </div>
                      </div>
                    )}

                    {/* Statistiques */}
                    <div className="space-y-3 mb-4">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Employés"
                            value={positionEmployees.length}
                            prefix={<UserOutlined />}
                            valueStyle={{ fontSize: "16px" }}
                          />
                        </Col>
                        <Col span={12}>
                          <div className="text-center">
                            <Text type="secondary" className="text-sm">
                              Statut
                            </Text>
                            <div className="mt-1">
                              <Tag
                                color={
                                  position.status === "active" ? "green" : "red"
                                }
                              >
                                {position.status === "active"
                                  ? "Actif"
                                  : "Inactif"}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {/* Fourchette salariale */}
                      {(position.min_salary || position.max_salary) && (
                        <div>
                          <Text type="secondary" className="text-sm">
                            Fourchette salariale :
                          </Text>
                          <div className="mt-1">
                            <Text strong className="text-sm">
                              {position.min_salary
                                ? formatCurrency(position.min_salary)
                                : "N/A"}{" "}
                              -{" "}
                              {position.max_salary
                                ? formatCurrency(position.max_salary)
                                : "N/A"}
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Employés dans ce poste */}
                    {positionEmployees.length > 0 && (
                      <div>
                        <Divider className="!my-3" />
                        <Text type="secondary" className="text-sm">
                          Employés :
                        </Text>
                        <List
                          size="small"
                          dataSource={positionEmployees.slice(0, 3)}
                          className="mt-2"
                          renderItem={(emp: HREmployee) => (
                            <List.Item className="!p-1">
                              <Text className="text-sm">
                                {emp.first_name} {emp.last_name}
                              </Text>
                            </List.Item>
                          )}
                        />
                        {positionEmployees.length > 3 && (
                          <Text type="secondary" className="text-xs">
                            +{positionEmployees.length - 3} autres...
                          </Text>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Card className="text-center">
            <Empty
              description={
                selectedDepartment
                  ? "Aucun poste trouvé dans ce département"
                  : "Aucun poste enregistré"
              }
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPosition(null);
                setShowForm(true);
              }}
              className="mt-4"
            >
              Créer le premier poste
            </Button>
          </Card>
        )}

        {/* Modal de formulaire */}
        <PositionForm
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingPosition(null);
          }}
          position={editingPosition}
          selectedDepartmentId={selectedDepartment}
        />
      </Content>
    </Layout>
  );
};

export default PositionList;
